import { useMutation, useSuspenseQuery } from '@apollo/client'
import { AdminAPI, getFragment, SharedAPI } from '@guestgoodie/graphql'
import { useCallback, useMemo } from 'react'
import { useLocation } from 'wouter'

export default function useCategory(
  layoutId: number,
  slug: string,
  withGoodie = false
) {
  const [update] = useMutation(AdminAPI.category.UPDATE)

  const { data } = useSuspenseQuery(AdminAPI.category.GET_BY_LAYOUT_AND_SLUG, {
    variables: { layoutId, slug, withLayout: true, withGoodie }
  })

  const category = useMemo(() => {
    return getFragment(
      SharedAPI.category.CATEGORY_FRAGMENT,
      data?.getCategoryByLayoutAndSlug
    )
  }, [data])

  const layout = useMemo(
    () => getFragment(SharedAPI.layout.LAYOUT_FRAGMENT, category?.layout),
    [category]
  )

  const [, navigate] = useLocation()

  const addGoodies = useCallback(() => {
    navigate(`/add-category-goodies/${layout?.version}/${slug}`)
  }, [navigate, layout, slug])

  const removeGoodies = useCallback(
    (ids: Set<number>) => {
      if (!category) return

      const categoryGoodies = category.categoryGoodies
        .toSorted(({ position: posA }, { position: posB }) => posA - posB)
        .filter(({ goodieId }) => !ids.has(goodieId))
        .map(({ goodieId }, position) => ({ goodieId, position }))

      const layout = getFragment(
        SharedAPI.layout.LAYOUT_FRAGMENT,
        category.layout
      )

      update({
        variables: {
          input: {
            id: category.id,
            goodieIds: categoryGoodies.map(({ goodieId }) => goodieId)
          }
        },
        optimisticResponse: {
          updateCategory: {
            ...category,
            __typename: 'Category',
            // Needs this else Apollo Typescript shouts, super annoying, but what are you going to do...
            cover: {
              ...getFragment(SharedAPI.file.FILE_FRAGMENT, category.cover),
              translations: getFragment(
                SharedAPI.file.FILE_FRAGMENT,
                category.cover
              ).translations?.map(trans =>
                getFragment(SharedAPI.translation.TRANSLATION_FRAGMENT, trans)
              )
            },
            layout: layout
              ? {
                  ...layout,
                  createdBy: getFragment(
                    SharedAPI.user.USER_FRAGMENT,
                    layout?.createdBy
                  ),
                  publishedBy: getFragment(
                    SharedAPI.user.USER_FRAGMENT,
                    layout?.publishedBy
                  )
                }
              : undefined,
            categoryGoodies,
            translations: category.translations?.map(trans =>
              getFragment(SharedAPI.translation.TRANSLATION_FRAGMENT, trans)
            )
          }
        }
      })
    },
    [category, update]
  )

  const moveGoodies = useCallback(
    (ids: Set<number>, position: number) => {
      if (!category) return
      if (position < 0 || position >= category.categoryGoodies.length) return

      const newGoodieSeq = category.categoryGoodies
        .toSorted(({ position: a }, { position: b }) => a - b)
        .map(({ goodieId }) => goodieId)
        .filter(id => !ids.has(id))
        .toSpliced(position, 0, ...Array.from(ids))

      const layout = getFragment(
        SharedAPI.layout.LAYOUT_FRAGMENT,
        category.layout
      )

      update({
        variables: {
          input: {
            id: category.id,
            goodieIds: newGoodieSeq
          }
        },
        optimisticResponse: {
          updateCategory: {
            ...category,
            __typename: 'Category',
            // Needs this else Apollo Typescript shouts, super annoying, but what are you going to do...
            cover: {
              ...getFragment(SharedAPI.file.FILE_FRAGMENT, category.cover),
              translations: getFragment(
                SharedAPI.file.FILE_FRAGMENT,
                category.cover
              ).translations?.map(trans =>
                getFragment(SharedAPI.translation.TRANSLATION_FRAGMENT, trans)
              )
            },
            layout: layout
              ? {
                  ...layout,
                  createdBy: getFragment(
                    SharedAPI.user.USER_FRAGMENT,
                    layout?.createdBy
                  ),
                  publishedBy: getFragment(
                    SharedAPI.user.USER_FRAGMENT,
                    layout?.publishedBy
                  )
                }
              : undefined,
            categoryGoodies: newGoodieSeq.map((goodieId, position) => ({
              goodieId,
              position,
              __typename: 'CategoryGoodie' as const
            })),
            translations: category.translations?.map(trans =>
              getFragment(SharedAPI.translation.TRANSLATION_FRAGMENT, trans)
            )
          }
        }
      })
    },
    [category, update]
  )

  return {
    category,
    addGoodies,
    moveGoodies,
    removeGoodies
  }
}
